import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, ArrowRight, Sparkles, Clock, LogIn } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [isGuest, setIsGuest] = useState(false);
  const [purchasedLevel, setPurchasedLevel] = useState('');
  const [purchaseType, setPurchaseType] = useState('');

  // Kill PostHog to prevent cross-origin iframe errors with PayPal
  useEffect(() => {
    const ph = window.posthog;
    if (ph) {
      if (typeof ph.stopSessionRecording === 'function') ph.stopSessionRecording();
      if (typeof ph.opt_out_capturing === 'function') ph.opt_out_capturing();
    }
    document.querySelectorAll('script[src*="posthog-recorder"]').forEach(s => s.remove());
    return () => { if (ph && typeof ph.opt_in_capturing === 'function') ph.opt_in_capturing(); };
  }, []);

  useEffect(() => {
    const reference = searchParams.get('reference');
    const provider = searchParams.get('provider') || 'paystack';
    const guest = searchParams.get('guest') === '1';
    const type = searchParams.get('type');
    const subscriptionId = searchParams.get('subscription_id') || sessionStorage.getItem('pending_subscription_id');
    setIsGuest(guest);

    if (type === 'subscription' && subscriptionId) {
      sessionStorage.removeItem('pending_subscription_id');
      sessionStorage.removeItem('pending_subscription_plan');
      verifySubscription(subscriptionId);
    } else if (reference) {
      if (provider === 'paypal') {
        verifyPaypal(reference);
      } else {
        verifyPaystack(reference);
      }
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const handleVerifySuccess = (data) => {
    const level = data.level || '';
    const planType = data.plan_type || '';
    setPurchasedLevel(level);
    setPurchaseType(planType);
    setStatus('success');

    // Auto-redirect authenticated level purchasers to exam date page
    if (user && planType === 'level' && level) {
      setTimeout(() => navigate(`/exam-date?level=${level}`), 2500);
    }
  };

  const verifySubscription = async (subscriptionId) => {
    try {
      const response = await api.get(`/payments/paypal/verify-subscription/${subscriptionId}`);
      if (response.data.status === 'active') {
        setPurchaseType('subscription');
        setStatus('success');
        if (user) {
          try { const u = await api.get('/auth/me'); updateUser(u.data); } catch (e) {}
        }
      } else {
        await new Promise(r => setTimeout(r, 3000));
        const retry = await api.get(`/payments/paypal/verify-subscription/${subscriptionId}`);
        if (retry.data.status === 'active') {
          setPurchaseType('subscription');
          setStatus('success');
          if (user) {
            try { const u = await api.get('/auth/me'); updateUser(u.data); } catch (e) {}
          }
        } else {
          setStatus('error');
        }
      }
    } catch (error) {
      console.error('Subscription verification error:', error);
      setStatus('error');
    }
  };

  const verifyPaypal = async (reference) => {
    try {
      const response = await api.get(`/payments/paypal/verify/${reference}`);
      if (response.data.payment_status === 'paid') {
        if (user) {
          try { const u = await api.get('/auth/me'); updateUser(u.data); } catch (e) {}
        }
        handleVerifySuccess(response.data);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('PayPal verification error:', error);
      setStatus('error');
    }
  };

  const verifyPaystack = async (reference) => {
    try {
      const response = await api.get(`/payments/verify/${reference}`);
      if (response.data.payment_status === 'paid') {
        if (user) {
          try { const u = await api.get('/auth/me'); updateUser(u.data); } catch (e) {}
        }
        handleVerifySuccess(response.data);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Paystack verification error:', error);
      setStatus('error');
    }
  };

  const handleGoogleSignIn = () => {
    // For level purchases, redirect to exam-date after login
    const redirectPath = purchasedLevel ? `/exam-date?level=${purchasedLevel}` : '/levels';
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl border border-border/50 text-center"
        data-testid="payment-result-card"
      >
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Processing Payment
            </h1>
            <p className="text-muted-foreground" data-testid="payment-checking-msg">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="payment-success-title">
              Payment Successful!
            </h1>

            {isGuest && !user ? (
              <>
                <p className="text-muted-foreground mb-6">
                  Your access is ready. Sign in with Google to {purchasedLevel ? 'set your exam date and ' : ''}start your training.
                </p>

                <div className="bg-primary/5 rounded-xl p-4 mb-6 border border-primary/10">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-medium">Your purchase will be automatically linked!</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use the same email you entered during checkout
                  </p>
                </div>

                <Button
                  className="w-full rounded-full py-6 font-bold"
                  onClick={handleGoogleSignIn}
                  data-testid="sign-in-google-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in with Google to Access Training
                </Button>
              </>
            ) : purchaseType === 'level' && purchasedLevel ? (
              <>
                <p className="text-muted-foreground mb-6">
                  Setting up your personalized study plan...
                </p>
                <div className="bg-primary/5 rounded-xl p-4 mb-6 border border-primary/10">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-medium">Redirecting to exam date setup</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <Clock className="w-3 h-3 inline mr-1" />
                    You'll set your official DELF exam date next
                  </p>
                </div>
                <Button
                  className="w-full rounded-full py-6 font-bold"
                  onClick={() => navigate(`/exam-date?level=${purchasedLevel}`)}
                  data-testid="set-exam-date-btn"
                >
                  Set My Exam Date
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-6">
                  Your access has been activated. Start studying now!
                </p>

                <div className="bg-primary/5 rounded-xl p-4 mb-6 border border-primary/10">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-medium">Your DELF journey begins now!</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Check your access details in the Pricing page
                  </p>
                </div>

                <Button
                  className="w-full rounded-full py-6 font-bold"
                  onClick={() => navigate('/levels')}
                  data-testid="start-learning-btn"
                >
                  Start Learning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="payment-error-title">
              Payment Failed
            </h1>
            <p className="text-muted-foreground mb-6">
              We couldn't confirm your payment. Please try again or contact support.
            </p>
            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => navigate('/pricing')}
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Button
                className="flex-1 rounded-full"
                onClick={() => navigate('/levels')}
                data-testid="go-to-levels-btn"
              >
                Go to Levels
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
